import { create } from 'zustand'
import { apiClient } from '../lib/api'
import { track } from '../lib/analytics'
import type { TimelineEntry, PaginatedResponse } from '../lib/types'
import { useThreadStore } from './threadStore'

interface ChatState {
  entries: TimelineEntry[]
  streaming: boolean
  streamingText: string
  error: string | null
  nextCursor: string | null
  abortController: AbortController | null
  interruptedEntryId: number | null
  // Entry whose answer was cut off (finish_reason != "stop") — shows a Continue affordance.
  truncatedEntryId: number | null
  // Entry currently being extended by continueStream — suppresses the bottom thinking
  // indicator/footer since we append into the existing bubble instead of a new one.
  continuingEntryId: number | null

  fetchTimeline: (threadId: number) => Promise<void>
  fetchMore: (threadId: number) => Promise<void>
  sendMessage: (threadId: number, message: string) => Promise<void>
  retryStream: (threadId: number, timelineEntryId: number) => Promise<void>
  continueStream: (threadId: number, timelineEntryId: number) => Promise<void>
  removeEntry: (threadId: number, entryId: number) => Promise<void>
  stopStreaming: () => void
  clearChat: () => void
}

export const useChatStore = create<ChatState>((set, get) => ({
  entries: [],
  streaming: false,
  streamingText: '',
  error: null,
  nextCursor: null,
  abortController: null,
  interruptedEntryId: null,
  truncatedEntryId: null,
  continuingEntryId: null,

  fetchTimeline: async (threadId) => {
    try {
      const data = await apiClient.get<PaginatedResponse<TimelineEntry>>(
        `research/threads/${threadId}/timeline/`
      )
      set({ entries: data.results, nextCursor: data.next, error: null })
    } catch {
      set({ error: 'Failed to load conversation.' })
    }
  },

  fetchMore: async (threadId) => {
    const { nextCursor } = get()
    if (!nextCursor) return
    const url = nextCursor.replace(/^.*\/api\/v1\//, '')
    const data = await apiClient.get<PaginatedResponse<TimelineEntry>>(url)
    set((s) => ({ entries: [...data.results, ...s.entries], nextCursor: data.next }))
  },

  sendMessage: async (threadId, message) => {
    const controller = new AbortController()
    set({ streaming: true, streamingText: '', error: null, abortController: controller, interruptedEntryId: null, truncatedEntryId: null })

    try {
      const { exchange_id, timeline_entry_id } = await apiClient.post<{
        exchange_id: number
        timeline_entry_id: number
      }>(`research/threads/${threadId}/exchanges/`, { message })
      track('exchange_sent', { thread_id: threadId, message_length: message.length })

      const userEntry: TimelineEntry = {
        id: timeline_entry_id,
        entry_type: 'exchange',
        position: get().entries.length + 1,
        created_at: new Date().toISOString(),
        exchange: {
          id: exchange_id,
          timeline_entry: timeline_entry_id,
          user_message: message,
          ai_response: '',
          user_message_tokens: 0,
          ai_response_tokens: 0,
          model_used: '',
          response_time_ms: null,
          cost: '0',
        },
        content_item: null,
      }
      set((s) => ({ entries: [...s.entries, userEntry] }))

      const response = await fetch(`/api/v1/threads/${threadId}/stream/`, {
        credentials: 'same-origin',
        signal: controller.signal,
      })

      if (!response.ok || !response.body) {
        throw new Error('Stream connection failed')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullResponse = ''
      let pendingEvent: string | null = null

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            pendingEvent = line.slice(7).trim()
          } else if (line.startsWith('data: ')) {
            const data = line.slice(6)

            if (pendingEvent === 'done') {
              track('exchange_completed', { thread_id: threadId, response_length: fullResponse.length })
              const prevCount = useThreadStore.getState().threads.find((t) => t.id === threadId)?.exchange_count || 0
              const newCount = prevCount + 1
              if ([1, 5, 10, 20].includes(newCount)) {
                track('thread_milestone', { thread_id: threadId, milestone: newCount })
              }
              // Parse done payload and update thread store
              let truncated = false
              try {
                const payload = JSON.parse(data)
                truncated = payload.truncated === true
                useThreadStore.setState((s) => ({
                  threads: s.threads.map((t) =>
                    t.id === threadId
                      ? {
                          ...t,
                          exchange_count: newCount,
                          ...(payload.new_title ? { title: payload.new_title } : {}),
                        }
                      : t
                  ),
                }))
              } catch {
                // no payload or invalid JSON — still increment exchange count
                useThreadStore.setState((s) => ({
                  threads: s.threads.map((t) =>
                    t.id === threadId
                      ? { ...t, exchange_count: newCount }
                      : t
                  ),
                }))
              }
              set((s) => ({
                streaming: false,
                streamingText: '',
                abortController: null,
                interruptedEntryId: null,
                // A cut-off answer (length/content_filter/abnormal end) offers Continue.
                truncatedEntryId: truncated && fullResponse ? timeline_entry_id : null,
                entries: s.entries.map((e) =>
                  e.id === timeline_entry_id && e.exchange
                    ? { ...e, exchange: { ...e.exchange, ai_response: fullResponse } }
                    : e
                ),
              }))
              return
            } else if (pendingEvent === 'error') {
              track('ai_error_shown', { thread_id: threadId, source: 'send' })
              // Keep whatever streamed so the answer doesn't vanish; mark the entry
              // interrupted so the UI shows the partial text plus a Retry beneath it.
              set((s) => ({
                streaming: false,
                streamingText: '',
                abortController: null,
                interruptedEntryId: fullResponse ? timeline_entry_id : null,
                error: fullResponse ? null : 'Response failed — tap Retry.',
                entries: fullResponse
                  ? s.entries.map((e) =>
                      e.id === timeline_entry_id && e.exchange
                        ? { ...e, exchange: { ...e.exchange, ai_response: fullResponse } }
                        : e
                    )
                  : s.entries,
              }))
              return
            } else {
              // Regular streaming token
              try {
                const token = JSON.parse(data)
                fullResponse += token
                set({ streamingText: fullResponse })
              } catch {
                // non-JSON data line
              }
            }

            pendingEvent = null
          }
        }
      }

      if (fullResponse) {
        set((s) => ({
          streaming: false,
          streamingText: '',
          abortController: null,
          interruptedEntryId: null,
          entries: s.entries.map((e) =>
            e.id === timeline_entry_id && e.exchange
              ? { ...e, exchange: { ...e.exchange, ai_response: fullResponse } }
              : e
          ),
        }))
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User stopped generation — finalize whatever we have
        const current = get().streamingText
        if (current) {
          set((s) => ({
            streaming: false,
            streamingText: '',
            abortController: null,
            entries: s.entries.map((e) =>
              e.exchange && e.exchange.ai_response === ''
                ? { ...e, exchange: { ...e.exchange, ai_response: current } }
                : e
            ),
          }))
        } else {
          set({ streaming: false, streamingText: '', abortController: null })
        }
      } else {
        track('ai_error_shown', { thread_id: threadId, source: 'send', kind: 'network' })
        set({ streaming: false, error: 'Failed to send message', abortController: null })
      }
    }
  },

  retryStream: async (threadId, timelineEntryId) => {
    const controller = new AbortController()
    // Clear any preserved partial on this entry so the fresh stream replaces it
    // instead of rendering stacked old + new text.
    set((s) => ({
      streaming: true,
      streamingText: '',
      error: null,
      abortController: controller,
      interruptedEntryId: null,
      truncatedEntryId: null,
      entries: s.entries.map((e) =>
        e.id === timelineEntryId && e.exchange
          ? { ...e, exchange: { ...e.exchange, ai_response: '' } }
          : e
      ),
    }))

    try {
      const response = await fetch(`/api/v1/threads/${threadId}/stream/`, {
        credentials: 'same-origin',
        signal: controller.signal,
      })

      if (!response.ok || !response.body) {
        throw new Error('Stream connection failed')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullResponse = ''
      let pendingEvent: string | null = null

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            pendingEvent = line.slice(7).trim()
          } else if (line.startsWith('data: ')) {
            const data = line.slice(6)

            if (pendingEvent === 'done') {
              // Retry regenerates the SAME exchange — do NOT increment exchange_count
              // (it was already counted when the exchange was first created).
              let truncated = false
              try {
                const payload = JSON.parse(data)
                truncated = payload.truncated === true
                if (payload.new_title) {
                  useThreadStore.setState((s) => ({
                    threads: s.threads.map((t) =>
                      t.id === threadId ? { ...t, title: payload.new_title } : t
                    ),
                  }))
                }
              } catch { /* ok */ }
              set((s) => ({
                streaming: false,
                streamingText: '',
                abortController: null,
                interruptedEntryId: null,
                truncatedEntryId: truncated && fullResponse ? timelineEntryId : null,
                entries: s.entries.map((e) =>
                  e.id === timelineEntryId && e.exchange
                    ? { ...e, exchange: { ...e.exchange, ai_response: fullResponse } }
                    : e
                ),
              }))
              return
            } else if (pendingEvent === 'error') {
              track('ai_error_shown', { thread_id: threadId, source: 'retry' })
              // Keep whatever streamed this attempt; mark interrupted so the kept text
              // stays visible with a Retry beneath it.
              set((s) => ({
                streaming: false,
                streamingText: '',
                abortController: null,
                interruptedEntryId: fullResponse ? timelineEntryId : null,
                error: fullResponse ? null : 'Response failed — tap Retry to try again.',
                entries: fullResponse
                  ? s.entries.map((e) =>
                      e.id === timelineEntryId && e.exchange
                        ? { ...e, exchange: { ...e.exchange, ai_response: fullResponse } }
                        : e
                    )
                  : s.entries,
              }))
              return
            } else {
              try {
                const token = JSON.parse(data)
                fullResponse += token
                set({ streamingText: fullResponse })
              } catch { /* non-JSON data */ }
            }

            pendingEvent = null
          }
        }
      }

      if (fullResponse) {
        set((s) => ({
          streaming: false,
          streamingText: '',
          abortController: null,
          interruptedEntryId: null,
          entries: s.entries.map((e) =>
            e.id === timelineEntryId && e.exchange
              ? { ...e, exchange: { ...e.exchange, ai_response: fullResponse } }
              : e
          ),
        }))
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        set({ streaming: false, streamingText: '', abortController: null })
      } else {
        track('ai_error_shown', { thread_id: threadId, source: 'retry', kind: 'network' })
        set({ streaming: false, error: 'Retry failed.', abortController: null })
      }
    }
  },

  continueStream: async (threadId, timelineEntryId) => {
    // Resume a truncated answer by appending into the SAME bubble. The backend appends to
    // the existing exchange; here we render base text + streamed continuation live.
    const controller = new AbortController()
    const entry = get().entries.find((e) => e.id === timelineEntryId)
    const base = entry?.exchange?.ai_response || ''
    set({
      streaming: true,
      streamingText: '',
      error: null,
      abortController: controller,
      interruptedEntryId: null,
      truncatedEntryId: null,
      continuingEntryId: timelineEntryId,
    })

    const applyText = (text: string) =>
      set((s) => ({
        entries: s.entries.map((e) =>
          e.id === timelineEntryId && e.exchange
            ? { ...e, exchange: { ...e.exchange, ai_response: text } }
            : e
        ),
      }))

    try {
      const response = await fetch(`/api/v1/threads/${threadId}/continue/`, {
        credentials: 'same-origin',
        signal: controller.signal,
      })
      if (!response.ok || !response.body) {
        throw new Error('Continue connection failed')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let addition = ''
      let pendingEvent: string | null = null

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            pendingEvent = line.slice(7).trim()
          } else if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (pendingEvent === 'done') {
              let truncated = false
              try { truncated = JSON.parse(data).truncated === true } catch { /* ok */ }
              applyText(base + addition)
              set({
                streaming: false,
                streamingText: '',
                abortController: null,
                continuingEntryId: null,
                truncatedEntryId: truncated && base + addition ? timelineEntryId : null,
              })
              return
            } else if (pendingEvent === 'error') {
              track('ai_error_shown', { thread_id: threadId, source: 'continue' })
              applyText(base + addition)
              set({
                streaming: false,
                streamingText: '',
                abortController: null,
                continuingEntryId: null,
                interruptedEntryId: base + addition ? timelineEntryId : null,
              })
              return
            } else {
              try {
                addition += JSON.parse(data)
                applyText(base + addition)
              } catch { /* non-JSON data */ }
            }
            pendingEvent = null
          }
        }
      }

      // Stream ended without an explicit done event — keep whatever streamed.
      applyText(base + addition)
      set({ streaming: false, streamingText: '', abortController: null, continuingEntryId: null })
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        set({ streaming: false, streamingText: '', abortController: null, continuingEntryId: null })
      } else {
        track('ai_error_shown', { thread_id: threadId, source: 'continue', kind: 'network' })
        set({ streaming: false, error: 'Continue failed.', abortController: null, continuingEntryId: null })
      }
    }
  },

  removeEntry: async (threadId, entryId) => {
    const oldEntries = get().entries
    // Optimistic removal
    set((s) => ({ entries: s.entries.filter((e) => e.id !== entryId) }))
    try {
      await apiClient.delete(`research/threads/${threadId}/timeline/${entryId}/`)
    } catch {
      // Revert: try to refetch, fall back to restoring snapshot
      try {
        await get().fetchTimeline(threadId)
      } catch {
        set({ entries: oldEntries, error: 'Failed to remove content. Please try again.' })
      }
    }
  },

  stopStreaming: () => {
    const { abortController } = get()
    if (abortController) {
      abortController.abort()
    }
  },

  clearChat: () => set({ entries: [], streaming: false, streamingText: '', error: null, abortController: null, interruptedEntryId: null, truncatedEntryId: null, continuingEntryId: null }),
}))
